import { alpha, styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { Link } from 'react-router-dom';

const rootStyles = ({ theme }: { theme: Theme }) => ({
  display: 'grid',
  flexGrow: 1,
  gridTemplateColumns: 'auto 1fr auto',
  gap: '0.625rem',
  lineHeight: 1.2,
  minHeight: '2.5rem',
  padding: '0.5rem',
  borderRadius: 4,
  cursor: 'pointer',
  color: 'inherit',
  textDecoration: 'none',
  transition: 'background-color 180ms',
  // The fill every MUI menu item uses — the `MuiMenuItem` hover in `mui.theme.tsx`
  // (`grey[120]`). This was its own translucent wash, so a related-links row lit up
  // differently from the menu row beside it in the same header.
  '&:hover': {
    backgroundColor: theme.palette.grey[120],
    '& .end-icon': {
      opacity: 1,
    },
  },
});

export const RootLink = styled(Link)(rootStyles);

export const RootStatic = styled('div')(({ theme }) => ({
  ...rootStyles({ theme }),
  cursor: 'default',
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

export const StartIcon = styled('span')(({ theme }) => ({
  width: 16,
  height: 16,
  marginTop: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: alpha(theme.palette.primary.darker, 0.8),
}));

export const EndIcon = styled('span')(({ theme }) => ({
  display: 'flex',
  color: theme.palette.grey[60],
  opacity: 0.9,
}));

export const Title = styled('div')(({ theme }) => ({
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  fontWeight: 600,
  fontSize: 12,
  color: theme.palette.primary.darker,
  marginBottom: '0.125rem',
}));

/**
 * The title of a row that cannot be opened from here yet (`disabledHint`): muted, but
 * at a readable contrast. The hint under it says why; `text.disabled` would not be
 * legible.
 */
export const MutedTitle = styled(Title)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

export const Content = styled('div')(() => ({
  minWidth: '7rem',
  maxWidth: '12rem',
  paddingRight: '0.5rem',
}));

export const Subtitle = styled('div')(({ theme }) => ({
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  fontWeight: 500,
  fontSize: 12,
  color: theme.palette.text.secondary,
}));

/** Why the row cannot be opened, in full: it wraps rather than being cut off. */
export const Hint = styled('div')(({ theme }) => ({
  marginTop: '0.25rem',
  fontSize: 12,
  lineHeight: 1.35,
  color: theme.palette.text.secondary,
}));
